import { useDispatch, useSelector } from "react-redux";
import {CombinedEvents} from "../../../helpers/projectEvents";
import {API, BookAPI, Endpoints, ProjectAPI} from "@brg/api";
import ProfileFoldersSlice, { ProfileFoldersSelector } from "shared/reducers/ProfileFoldersSlice";
import { useFoldersController } from "shared/hooks/useFoldersController";
import urlApi from "shared/actions/urlApi";
import { useCallback } from "react";
import { useModal } from "core";
import { SUBSCRIBE_VISIBILITY_STATUS } from "shared/components/Subscribe/const";

const PROJECT_DELETE_STATUS = 8;
const PROJECT_DRAFT_STATUS = 0;

export const useGetDesignerHandlers = (updateTabs: any, draftProjects: any, toasts: any, t: any): CombinedEvents => {
    const dispatch = useDispatch();
    const modal = useModal();
    const { foldersList } = useSelector(ProfileFoldersSelector);
    const { onRemoveProject } = useFoldersController();

    const onDeleteFromFolder = async (id: string) => {
        const initFolders = async (profileId: string) => {
            const data = await urlApi().fetchFolders(profileId ?? '')

            if (Array.isArray(data)) {
            dispatch(ProfileFoldersSlice.actions.setFolders(data))
            };
        }

      if (!foldersList?.length) return;

      const tFolder = foldersList?.find((folder) => folder?.entityIds?.includes(id))

      if (tFolder) {
        onRemoveProject({...tFolder, entityIds: [id]}).then((r) => {
          initFolders(tFolder?.accountId ?? '');
        })
      }

    }

    const onStatusChange = (projectId: string, status: number, confirmationText?: string) => {
        const confirmed = confirmationText ? window.confirm(confirmationText) : true;
        if (confirmed) {
            const isDelete = status === 8;
            const bookId = isDelete && draftProjects?.find((i: any) => i?.projectId === projectId)?.bookId;

            if (bookId && !!bookId?.length) {
                // const bookName = draftBooksAuthor?.find((b) => b?.bookId === bookId)?.title || ''; // Название книги (если потреб)
                toasts.error(t('book_edit.frames.book_cant_delete'))
                return;
            }

            ProjectAPI.UpdateStatus({
                ProjectId: projectId,
                projectStatus: status,
            } as any)
                .then(() => {
                    if (status === PROJECT_DELETE_STATUS) {
                        toasts.info(t('toasts.project_delete'))
                    }

                    if ([PROJECT_DRAFT_STATUS, PROJECT_DELETE_STATUS]?.includes(status)) {
                        onDeleteFromFolder(projectId ?? '')
                    }
                })
                .finally(() => {
                    updateTabs()
                });
        }
    }

    const onBookStatusChange = (bookId: string, status: number, confirmationText?: string) => {
        const confirmed = confirmationText ? window.confirm(confirmationText) : true;
        if (confirmed) {
            BookAPI.UpdateStatus({
                bookId: bookId,
                bookStatus: status,
            } as any)
                .then(() => {

                    if (status === PROJECT_DELETE_STATUS) {
                        toasts.info(t('toasts.book_delete'))
                    }
                    updateTabs();
                });
        }
    }

    const onNotParticipateCompetition = (projectId: string, competitionId: string) => {
        ProjectAPI.NotParticipateCompetition({
            projectId,
            competitionId
        })
        .then((r) => {
            if (r.status === 403) {
                toasts.error(t('project_edit.frames.forbidden_competition_participante'))
                return;
            }
            updateTabs()
        });
    }

    const onUnsubscribe = useCallback((subscriberId: string, targetId: string) => {

        if (!subscriberId || !targetId) return;

        API.request()
        .endpoint(Endpoints.subscriptions)
        .url('/api/Subscription/Unsubscribe')
        .post()
        .body({
            subscriberId: targetId,
            subscriberType: 0,
            targetId: subscriberId,
            targetType: 'userProjects',
        })
        .send()
        .then((r) => {
            if (r?.status === 200) {
                toasts.info('Подписка приостановлена')
                updateTabs()
            }
        })
    } , [])

    const onCancelSubscribe = useCallback((subscriberId: string, targetId: string, name?: string) => {

        if (!subscriberId || !targetId) return;

        const onSubmit = async () => {
            API.request()
            .endpoint(Endpoints.subscriptions)
            .url('/api/Subscription/CancelSubscription')
            .post()
            .body({
                subscriberId: targetId,
                subscriberType: 0,
                targetId: subscriberId,
                targetType: 'userProjects',
            })
            .send()
            .then((r) => {
                if (r?.status === 200) {
                    toasts.info('Подписка отменена');
                    updateTabs();
                    modal.hide();
                }
            })
        }

        
        modal.show('template-modal', {
            mainText: t('subscription.modal.cancel_subscribe.mainText'),
            secondText: t('subscription.modal.cancel_subscribe.secondText', { name: name || ''}),
            confirmText: t('subscription.modal.cancel_subscribe.confirmText'),
            cancelText: t('subscription.modal.cancel_subscribe.cancelText'),
            onConfirm: onSubmit,
            onSecond: () => modal.hide()
        })

    } , [modal])

    const onSubscribeClick = useCallback((subscriberId: string, targetId: string, tagIds: string[]) => {

        if (!subscriberId || !targetId) return;

        API.request()
        .endpoint(Endpoints.subscriptions)
        .url('/api/Subscription/Subscribe')
        .post()
        .body({
            subscriberId: subscriberId,
            subscriberType: 0,
            targetId: targetId,
            targetType: 'userProjects',
            ...(tagIds?.length && { tagIds })
        })
        .send()
        .then((r) => {
            if (r?.status === 200) {
                updateTabs()
            }
        })
    } , [])

    const onEditSubscribe = useCallback((targetId?: string) => {
        if (!targetId) return;
        modal.show('subscribe-user', {
            accountId: targetId,
            isResumeSubscribe: true,
            onSubmit: (subscriberId: string, targetId: string, tagIds: string[]) => 
                {
                    onSubscribeClick(subscriberId, targetId, tagIds)
                    updateTabs()
                },
        })
    } , [])

    const onUpdateSubVisibility = useCallback((
        subscriberId: string, 
        targetId: string, 
        mode: SUBSCRIBE_VISIBILITY_STATUS
    ) => {
        API.request()
        .endpoint(Endpoints.subscriptions)
        .url('/api/Subscription/UpdateVisibility')
        .post()
        .body({
            subscriberId: targetId,
            subscriberType: 0,
            targetId: subscriberId,
            targetType: 'userProjects',
            visibility: mode
        })
        .send()
        .then((r) => {
            if (r?.status === 200) {
                if (mode === SUBSCRIBE_VISIBILITY_STATUS.hidden) {
                    toasts.info(t('subscription.subscription_hidden'));
                }
                updateTabs()
            }
        })
    } ,[])

    return {
        onStatusChange,
        onBookStatusChange,
        onNotParticipateCompetition,
        onMoveDrafts: (projectId: string) => onStatusChange(projectId, 0, t('project.confirmation_msg.to_drafts')),
        onDelete: (projectId: string) => onStatusChange(projectId, 8, t('project.confirmation_msg.delete')),
        onPublish: (projectId: string) => onStatusChange(projectId, 1, t('project.confirmation_msg.publish')),
        onMoveModerate: (projectId: string) => onStatusChange(projectId, 5),
        onBookMoveDrafts: (projectId: string) => onBookStatusChange(projectId, 0, t('book_edit.confirmation_msg.to_drafts')),
        onBookDelete: (projectId: string) => onBookStatusChange(projectId, 8, t('book_edit.confirmation_msg.delete')),
        onBookPublish: (projectId: string) => onBookStatusChange(projectId, 1, t('book_edit.confirmation_msg.publish')),
        onUpdate: () => updateTabs(),
        onUnsubscribe,
        onCancelSubscribe,
        onEditSubscribe,
        onUpdateSubVisibility
    }
}import { useDispatch, useSelector } from "react-redux";
import {CombinedEvents} from "../../../helpers/projectEvents";
import {API, BookAPI, Endpoints, ProjectAPI} from "@brg/api";
import ProfileFoldersSlice, { ProfileFoldersSelector } from "shared/reducers/ProfileFoldersSlice";
import { useFoldersController } from "shared/hooks/useFoldersController";
import urlApi from "shared/actions/urlApi";
import { useCallback } from "react";
import { useModal } from "core";
import { SUBSCRIBE_VISIBILITY_STATUS } from "shared/components/Subscribe/const";

const PROJECT_DELETE_STATUS = 8;
const PROJECT_DRAFT_STATUS = 0;

export const useGetDesignerHandlers = (updateTabs: any, draftProjects: any, toasts: any, t: any): CombinedEvents => {
    const dispatch = useDispatch();
    const modal = useModal();
    const { foldersList } = useSelector(ProfileFoldersSelector);
    const { onRemoveProject } = useFoldersController();

    const onDeleteFromFolder = async (id: string) => {
        const initFolders = async (profileId: string) => {
            const data = await urlApi().fetchFolders(profileId ?? '')

            if (Array.isArray(data)) {
            dispatch(ProfileFoldersSlice.actions.setFolders(data))
            };
        }

      if (!foldersList?.length) return;

      const tFolder = foldersList?.find((folder) => folder?.entityIds?.includes(id))

      if (tFolder) {
        onRemoveProject({...tFolder, entityIds: [id]}).then((r) => {
          initFolders(tFolder?.accountId ?? '');
        })
      }

    }

    const onStatusChange = (projectId: string, status: number, confirmationText?: string) => {
        const confirmed = confirmationText ? window.confirm(confirmationText) : true;
        if (confirmed) {
            const isDelete = status === 8;
            const bookId = isDelete && draftProjects?.find((i: any) => i?.projectId === projectId)?.bookId;

            if (bookId && !!bookId?.length) {
                // const bookName = draftBooksAuthor?.find((b) => b?.bookId === bookId)?.title || ''; // Название книги (если потреб)
                toasts.error(t('book_edit.frames.book_cant_delete'))
                return;
            }

            ProjectAPI.UpdateStatus({
                ProjectId: projectId,
                projectStatus: status,
            } as any)
                .then(() => {
                    if (status === PROJECT_DELETE_STATUS) {
                        toasts.info(t('toasts.project_delete'))
                    }

                    if ([PROJECT_DRAFT_STATUS, PROJECT_DELETE_STATUS]?.includes(status)) {
                        onDeleteFromFolder(projectId ?? '')
                    }
                })
                .finally(() => {
                    updateTabs()
                });
        }
    }

    const onBookStatusChange = (bookId: string, status: number, confirmationText?: string) => {
        const confirmed = confirmationText ? window.confirm(confirmationText) : true;
        if (confirmed) {
            BookAPI.UpdateStatus({
                bookId: bookId,
                bookStatus: status,
            } as any)
                .then(() => {

                    if (status === PROJECT_DELETE_STATUS) {
                        toasts.info(t('toasts.book_delete'))
                    }
                    updateTabs();
                });
        }
    }

    const onNotParticipateCompetition = (projectId: string, competitionId: string) => {
        ProjectAPI.NotParticipateCompetition({
            projectId,
            competitionId
        })
        .then((r) => {
            if (r.status === 403) {
                toasts.error(t('project_edit.frames.forbidden_competition_participante'))
                return;
            }
            updateTabs()
        });
    }

    const onUnsubscribe = useCallback((subscriberId: string, targetId: string) => {

        if (!subscriberId || !targetId) return;

        API.request()
        .endpoint(Endpoints.subscriptions)
        .url('/api/Subscription/Unsubscribe')
        .post()
        .body({
            subscriberId: targetId,
            subscriberType: 0,
            targetId: subscriberId,
            targetType: 'userProjects',
        })
        .send()
        .then((r) => {
            if (r?.status === 200) {
                toasts.info('Подписка приостановлена')
                updateTabs()
            }
        })
    } , [])

    const onCancelSubscribe = useCallback((subscriberId: string, targetId: string, name?: string) => {

        if (!subscriberId || !targetId) return;

        const onSubmit = async () => {
            API.request()
            .endpoint(Endpoints.subscriptions)
            .url('/api/Subscription/CancelSubscription')
            .post()
            .body({
                subscriberId: targetId,
                subscriberType: 0,
                targetId: subscriberId,
                targetType: 'userProjects',
            })
            .send()
            .then((r) => {
                if (r?.status === 200) {
                    toasts.info('Подписка отменена');
                    updateTabs();
                    modal.hide();
                }
            })
        }

        
        modal.show('template-modal', {
            mainText: t('subscription.modal.cancel_subscribe.mainText'),
            secondText: t('subscription.modal.cancel_subscribe.secondText', { name: name || ''}),
            confirmText: t('subscription.modal.cancel_subscribe.confirmText'),
            cancelText: t('subscription.modal.cancel_subscribe.cancelText'),
            onConfirm: onSubmit,
            onSecond: () => modal.hide()
        })

    } , [modal])

    const onSubscribeClick = useCallback((subscriberId: string, targetId: string, tagIds: string[]) => {

        if (!subscriberId || !targetId) return;

        API.request()
        .endpoint(Endpoints.subscriptions)
        .url('/api/Subscription/Subscribe')
        .post()
        .body({
            subscriberId: subscriberId,
            subscriberType: 0,
            targetId: targetId,
            targetType: 'userProjects',
            ...(tagIds?.length && { tagIds })
        })
        .send()
        .then((r) => {
            if (r?.status === 200) {
                updateTabs()
            }
        })
    } , [])

    const onEditSubscribe = useCallback((targetId?: string) => {
        if (!targetId) return;
        modal.show('subscribe-user', {
            accountId: targetId,
            isResumeSubscribe: true,
            onSubmit: (subscriberId: string, targetId: string, tagIds: string[]) => 
                {
                    onSubscribeClick(subscriberId, targetId, tagIds)
                    updateTabs()
                },
        })
    } , [])

    const onUpdateSubVisibility = useCallback((
        subscriberId: string, 
        targetId: string, 
        mode: SUBSCRIBE_VISIBILITY_STATUS
    ) => {
        API.request()
        .endpoint(Endpoints.subscriptions)
        .url('/api/Subscription/UpdateVisibility')
        .post()
        .body({
            subscriberId: targetId,
            subscriberType: 0,
            targetId: subscriberId,
            targetType: 'userProjects',
            visibility: mode
        })
        .send()
        .then((r) => {
            if (r?.status === 200) {
                if (mode === SUBSCRIBE_VISIBILITY_STATUS.hidden) {
                    toasts.info(t('subscription.subscription_hidden'));
                }
                updateTabs()
            }
        })
    } ,[])

    return {
        onStatusChange,
        onBookStatusChange,
        onNotParticipateCompetition,
        onMoveDrafts: (projectId: string) => onStatusChange(projectId, 0, t('project.confirmation_msg.to_drafts')),
        onDelete: (projectId: string) => onStatusChange(projectId, 8, t('project.confirmation_msg.delete')),
        onPublish: (projectId: string) => onStatusChange(projectId, 1, t('project.confirmation_msg.publish')),
        onMoveModerate: (projectId: string) => onStatusChange(projectId, 5),
        onBookMoveDrafts: (projectId: string) => onBookStatusChange(projectId, 0, t('book_edit.confirmation_msg.to_drafts')),
        onBookDelete: (projectId: string) => onBookStatusChange(projectId, 8, t('book_edit.confirmation_msg.delete')),
        onBookPublish: (projectId: string) => onBookStatusChange(projectId, 1, t('book_edit.confirmation_msg.publish')),
        onUpdate: () => updateTabs(),
        onUnsubscribe,
        onCancelSubscribe,
        onEditSubscribe,
        onUpdateSubVisibility
    }
}